import { SvgIconComponent } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Stack, Typography, useTheme } from '@mui/material';
import React from 'react';

interface ContactCardProps {
  bulletPoints: { icon?: SvgIconComponent; label: string }[];
  buttonIcon: React.ReactNode;
  buttonText: string;
  description: string;
  icon: SvgIconComponent;
  onButtonClick: () => void;
  title: string;
}

export const ContactCard: React.FC<ContactCardProps> = ({
  bulletPoints,
  buttonIcon,
  buttonText,
  description,
  icon: Icon,
  onButtonClick,
  title,
}) => {
  const theme = useTheme();

  return (
    <Card
      sx={{
        '&:hover': {
          borderColor: theme.colors.borderStrong,
          transform: 'translateY(-2px)',
        },
        background: theme.colors.bgCard,
        border: `1px solid ${theme.colors.border}`,
        borderRadius: '20px',
        boxShadow: 'none',
        height: { md: 466, xs: 'auto' },
        position: 'relative',
        transition: 'transform 0.2s ease, border-color 0.2s ease',
      }}
    >
      <CardContent
        sx={{
          height: '100% !important',
          p: { md: 4, xs: 3 },
          pb: { md: '32px !important', xs: '24px !important' },
        }}
      >
        <Stack
          spacing={0}
          sx={{
            height: '100% !important',
            textAlign: 'left',
          }}
        >
          <Box
            sx={{
              alignItems: 'center',
              background: theme.colors.bgCard3,
              border: `1px solid ${theme.colors.border}`,
              borderRadius: '14px',
              display: 'flex',
              height: 52,
              justifyContent: 'center',
              mb: 3.5,
              width: 52,
            }}
          >
            <Icon
              sx={{
                color: theme.colors.textPrimary,
                fontSize: '26px',
              }}
            />
          </Box>
          <Stack spacing={0}>
            <Typography
              sx={{
                color: theme.colors.textPrimary,
                fontSize: 18,
                fontWeight: 800,
                letterSpacing: 0,
                lineHeight: 1.25,
                mb: 1,
              }}
            >
              {title}
            </Typography>
            <Typography
              sx={{
                color: theme.colors.textSecondary,
                fontSize: 14,
                fontWeight: 500,
                lineHeight: 1.5,
                mb: 2.5,
              }}
            >
              {description}
            </Typography>
            <Stack spacing={1.2} sx={{ mb: 4 }}>
              {bulletPoints.map(({ icon: ItemIcon, label }) => (
                <Stack
                  direction="row"
                  key={label}
                  spacing={1.4}
                  sx={{
                    alignItems: 'center',
                    color: theme.colors.textSecondary,
                    minHeight: 20,
                  }}
                >
                  {ItemIcon && (
                    <ItemIcon
                      sx={{
                        color: theme.colors.iconMuted,
                        flexShrink: 0,
                        fontSize: 16,
                      }}
                    />
                  )}
                  <Typography
                    sx={{
                      color: theme.colors.textSecondary,
                      fontSize: 14,
                      fontWeight: 500,
                      letterSpacing: 0,
                      lineHeight: 1.35,
                    }}
                  >
                    {label}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
          <Button
            onClick={onButtonClick}
            startIcon={buttonIcon}
            sx={{
              '&:hover': {
                background: theme.colors.bgHover,
                borderColor: theme.colors.borderStrong,
              },
              '& svg': {
                fontSize: '17px !important',
              },
              border: `1px solid ${theme.colors.border}`,
              borderRadius: '999px',
              color: theme.colors.textPrimary,
              fontSize: 14,
              fontWeight: 700,
              height: 44,
              maxWidth: '100%',
              mt: 'auto',
              px: 3,
              textTransform: 'none',
              width: { sm: 'fit-content', xs: '100%' },
            }}
            variant="outlined"
          >
            {buttonText}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};
